import { z } from 'zod';
import { localeSchema, timestampSchema } from './common';
import { userStatusSchema } from './auth';

/**
 * Who may reach this deployment, managed from the provider console instead of
 * `.env` (architecture: `platform.access_control.manage`).
 *
 * Two allowlists live here, both gated by the same permission because they are
 * reviewed together -- an email domain and an Entra tenant ID are both answers
 * to "who is allowed to sign in", just at different layers of the identity
 * provider.
 */

/**
 * A domain, lowercased, with no leading `@`. Backed by a CHECK constraint at
 * the database layer too (access_control_integrity migration) -- this schema
 * is the first line, not the only one.
 */
export const emailDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(255)
  .regex(
    /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/,
    'must be a domain, like "example.com"',
  );

/**
 * A person who signs in for the first time with a verified email at `domain`
 * is provisioned with `role` instead of the deployment's single global
 * default role (`AuthService.provision`). The role referenced is always
 * tenant-scoped -- enforced at write time and again by a database trigger, so
 * auto-provisioning can never hand out the platform role.
 */
export const allowedEmailDomainSchema = z.object({
  id: z.string().uuid(),
  domain: emailDomainSchema,
  role: z.object({ id: z.string().uuid(), key: z.string(), nameKey: z.string() }),
  /**
   * The organization a person provisioned this way joins; null for the host.
   * How a supplier's people arrive in the supplier's organization on their
   * first sign-in (docs/organizations-design.md).
   */
  party: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  createdAt: timestampSchema,
});
export type AllowedEmailDomain = z.infer<typeof allowedEmailDomainSchema>;

export const createAllowedEmailDomainSchema = z
  .object({
    domain: emailDomainSchema,
    roleId: z.string().uuid(),
    /** Left out or null: the host. An external organization needs a role
     *  an external member may hold, which the API checks. */
    partyId: z.string().uuid().nullable().optional(),
  })
  .strict();
export type CreateAllowedEmailDomain = z.infer<typeof createAllowedEmailDomainSchema>;

/** An Entra tenant GUID -- the ID token's `tid` claim. */
export const entraTenantIdSchema = z.string().uuid();

export const allowedTenantIdSchema = z.object({
  id: z.string().uuid(),
  tenantId: entraTenantIdSchema,
  /** A human note for the console -- "Contoso Ltd" -- never interpreted. */
  label: z.string().nullable(),
  createdAt: timestampSchema,
});
export type AllowedTenantId = z.infer<typeof allowedTenantIdSchema>;

export const createAllowedTenantIdSchema = z
  .object({
    tenantId: entraTenantIdSchema,
    label: z.string().max(200).nullish(),
  })
  .strict();
export type CreateAllowedTenantId = z.infer<typeof createAllowedTenantIdSchema>;

/**
 * The tenant allowlist only gates anything when the configured OIDC issuer is
 * one of Microsoft's multi-tenant aliases (`configuration.ts`). Carried on the
 * response rather than inferred client-side, so the console can show an
 * informational note instead of a management table it would be misleading to
 * offer.
 */
export const allowedTenantIdsResponseSchema = z.object({
  requiresAllowlist: z.boolean(),
  entries: z.array(allowedTenantIdSchema),
});
export type AllowedTenantIdsResponse = z.infer<typeof allowedTenantIdsResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Platform administrators                                                     */
/* -------------------------------------------------------------------------- */

/** A user account holding the platform (`system-administrator`) role. */
export const platformAdminSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string(),
  status: userStatusSchema,
  grantedAt: timestampSchema,
});
export type PlatformAdmin = z.infer<typeof platformAdminSchema>;

/**
 * Two ways to grant the platform role: to an account that already exists in
 * this organization, or -- mirroring `inviteUserSchema` -- to a brand-new
 * person by email, creating an `invited` account for them to claim at first
 * login (auth.service.ts case 2).
 */
export const grantPlatformAdminSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('existing'), userId: z.string().uuid() }).strict(),
  z
    .object({
      kind: z.literal('invite'),
      email: z.string().email().max(320),
      displayName: z.string().min(1).max(200),
      locale: localeSchema.default('en'),
    })
    .strict(),
]);
export type GrantPlatformAdmin = z.infer<typeof grantPlatformAdminSchema>;
